/**
 * KAN-756: таблица клиентов CRM.
 * KAN-757: одна таблица на странице: первым столбцом "Дата" (отметка внимания
 * или дата отработки), поля правятся прямо в ячейке (useCrmRowEdit), столбцы
 * разделены вертикальными линиями. Обёртка высотой с экран прокручивается в
 * обе стороны: шапка закреплена сверху, "Действия" справа, горизонтальная
 * полоса прокрутки всегда видна внизу экрана.
 * KAN-758: высоту считает useFillViewportHeight (верх обёртки до низа экрана).
 * KAN-759: столбец "Этап"; заказы открываются модалками (useCrmOrderDialogs).
 * KAN-760: этап правится в ячейке (список этапов доски).
 * KAN-765: "Телефон" и "Контакт" первыми и закреплены слева при горизонтальной
 * прокрутке (Телефон фиксированной ширины, Контакт сдвинут на неё, тень справа);
 * у контакта иконка "Переписка" открывает модалку переписки клиента.
 * KAN-766: fullscreen: блок развёрнут на весь экран, высота до самого низа
 * экрана (без нижнего отступа и на любом экране), смена режима пересчитывает её.
 * KAN-780: кнопка заказа (создать черновик или открыть текущий) в ячейке
 * "Действия" одной строкой с кнопками дашборда.
 * KAN-783: столбцы настроенных атрибутов лида (board.attributes), заголовок =
 * название атрибута, правятся в ячейке.
 * KAN-810: строка подкрашена по теплоте лида, бейдж теплоты в ячейке "Контакт"
 * (без нового столбца). Закреплённые ячейки берут фон строки (bg-inherit),
 * полоса слева рисуется на первой ячейке: у border-separate рамка <tr> не видна.
 * KAN-815: в ячейке "Причина" под текстом компактно следующее касание бота
 * (иконка и время, полная строка в подсказке), без нового столбца.
 * KAN-880: таблица лидов (columnLabels задан): вместо "Дата" столбцы "Группа"
 * (колонка доски, отметка внимания) и "Срок" (дни на этапе или в работе);
 * footer ("Показать ещё") внутри прокрутки под таблицей.
 * KAN-885: groupByDay - перед строками каждого дня строка-заголовок дня (одна ячейка
 * на всю ширину, не столбец шапки); группы даёт groupRowsByTakenDay (KAN-973: по period_at).
 * KAN-883: у таблицы лидов столбец "Взят" - дата и время взятия в работу.
 * KAN-958: шапка и ячейки рисуются по списку столбцов (lib/crmTableColumns, порядок
 * задан только там): важные столбцы сразу за Телефоном и Контактом. Скрытые столбцы
 * (hiddenColumns) не рисуются; у скрываемых в шапке кнопка-глаз (onHideColumn).
 * "Заказы" и "Текущий заказ" объединены в один столбец (CrmOrdersCell).
 * KAN-957: таблица снимает нижний отступ кабинета (useReleaseBottomGutter) и тянется
 * до низа экрана; под строками внутри прокрутки место под кнопку подсказки. Длинный
 * текст ячеек обрезан (причина 3 строки, организация, город, атрибуты 2), полный в title.
 */
import { Fragment, useMemo, type ReactNode } from 'react';
import { EyeOff } from 'lucide-react';
import { NextBotTouchNote } from '@/components/clients/NextBotTouchNote';
import { OrderComposeDialog } from '@/components/orders/OrderComposeDialog';
import { OrderViewDialog } from '@/components/orders/OrderViewDialog';
import { useCrmOrderDialogs } from '@/hooks/useCrmOrderDialogs';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useCrmRowEdit } from '@/hooks/useCrmRowEdit';
import { useFillViewportHeight } from '@/hooks/useFillViewportHeight';
import { useReleaseBottomGutter } from '@/components/layout/bottomGutter';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { leadTemperatureCellAccent, leadTemperatureStyle } from '@/lib/leadTemperature';
import { tableRowKind, type CrmTableRow } from '@/lib/crmTable';
import { attributeColumnCode, crmTableColumns, type CrmTableColumn } from '@/lib/crmTableColumns';
import { groupRowsByTakenDay } from '@/lib/crmTablePeriod';
import type { CrmAttribute, CrmFunnelStage, CrmRow, CrmRowPatch } from '@/types/crm';
import {
  CrmDateCell,
  CrmLeadColumnCell,
  CrmLeadDaysCell,
  CrmOrdersCell,
  CrmTakenCell,
  OrderActionButton,
  ReasonCell,
} from './CrmCells';
import {
  CrmAttributeField,
  CrmCommentField,
  CrmContactField,
  CrmManagerField,
  CrmNextContactField,
  CrmStageField,
  CrmTelegramField,
  CrmTextField,
  CrmCompanyField,
  type CrmRowSave,
} from './CrmFieldEditors';
import { ANY_UUID } from '@/lib/managerAccess';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';

interface CrmTableProps {
  items: CrmTableRow[];
  renderActions: (item: CrmTableRow) => ReactNode;
  testId?: string;
  /** KAN-760: этапы воронки для правки этапа (пусто: только просмотр). */
  stages?: readonly CrmFunnelStage[];
  /** KAN-766: блок развёрнут на весь экран. */
  fullscreen?: boolean;
  /** KAN-783: столбцы атрибутов лида (board.attributes); пусто: нет столбцов. */
  attributes?: readonly CrmAttribute[];
  /** KAN-880: подписи колонок доски по key: таблица лидов (столбцы группы и срока). */
  columnLabels?: Record<string, string>;
  /** KAN-880: под таблицей внутри прокрутки ("Показать ещё"). */
  footer?: ReactNode;
  /** KAN-885: заголовки дней взятия в работу перед строками дня. */
  groupByDay?: boolean;
  /** KAN-958: коды скрытых столбцов (обязательные не скрываются). */
  hiddenColumns?: ReadonlySet<string>;
  /** KAN-958: кнопка-глаз в шапке скрываемого столбца; не передан - кнопок нет. */
  onHideColumn?: (code: string) => void;
}

/** KAN-766: на весь экран таблица тянется до низа экрана и на низком экране. */
const FULLSCREEN_FILL = { bottomOffset: 0, smallScreenHeight: 0 } as const;
const NORMAL_FILL = {} as const;
const NO_HIDDEN: ReadonlySet<string> = new Set();
const NO_ATTRIBUTES: readonly CrmAttribute[] = [];
const NO_LABELS: Record<string, string> = {};

// border-separate: в режиме collapse границы не едут за закреплёнными ячейками.
const CELL = 'border-b border-r border-gray-200 px-2.5 last:border-r-0';
const TH_BASE = `${CELL} sticky top-0 bg-gray-50 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500`;
const TH = `${TH_BASE} whitespace-nowrap`;
/**
 * KAN-911: шапка столбца "по содержимому". Таблица w-full min-w-[1400px] раздаёт
 * лишнюю ширину всем столбцам без ширины; w-px сжимает столбец до содержимого
 * (min-w на td), и простор уходит в Организацию и Комментарий. Подпись может
 * переноситься по словам ("Следующая связь", названия атрибутов), чтобы
 * длинная подпись не держала ширину столбца.
 */
const TH_SHRINK = `${TH_BASE} w-px whitespace-normal`;
/** KAN-911: столбцы, сжатые по содержимому (и все столбцы атрибутов). */
const SHRINK_COLUMNS: ReadonlySet<string> = new Set(['reason', 'stage', 'next_contact']);
const TD = `${CELL} py-1.5 align-top`;
const TD_NOWRAP = `${TD} whitespace-nowrap`;
/**
 * KAN-908: закрепление столбцов только с md (768px). На телефоне закреплённые
 * Телефон, Контакт и Действия занимали почти весь экран, поэтому там обычная
 * горизонтальная прокрутка всей строки. sticky, отступы и тени только с md:.
 */
/** Закреплённый справа столбец действий: свой фон и тень слева при прокрутке. */
const STICKY_RIGHT = 'md:sticky md:right-0 border-l md:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]';
/**
 * KAN-765: закреплённые слева. Ширина Телефона фиксирована (border-box вместе с
 * отступами и рамкой), Контакт сдвинут ровно на неё; тень справа у Контакта.
 * 140px стоит в двух местах (w-/min-w- у Телефона и left- у Контакта): менять
 * вместе, иначе Контакт съедет. break-all: длинный номер переносится, а не
 * раздвигает столбец (max-w на td не работает).
 */
const STICKY_PHONE = 'md:sticky md:left-0 w-[140px] min-w-[140px] break-all';
const STICKY_CONTACT = 'md:sticky md:left-[140px] min-w-[150px] md:shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]';
/** Шапка закреплённых выше всех, тело выше обычных ячеек; фон непрозрачный. */
const PINNED_TH = 'z-[3]';
const PINNED_TD = 'z-[1]';
/** Фон закреплённой ячейки: белый или фон подкрашенной строки (KAN-810). */
const pinnedBg = (tinted: boolean): string => (tinted ? 'bg-inherit' : 'bg-white');
/** KAN-885: строка-заголовок дня взятия в работу. */
const DAY_HEADER = 'border-b border-gray-200 bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-700';

/** Класс шапки по коду столбца. */
function headerClass(code: string): string {
  if (code === 'phone') return `${TH} ${STICKY_PHONE} ${PINNED_TH}`;
  if (code === 'contact') return `${TH} ${STICKY_CONTACT} ${PINNED_TH}`;
  if (code === 'actions') return `${TH} ${STICKY_RIGHT} z-[3]`;
  const shrink = SHRINK_COLUMNS.has(code) || code.startsWith('attr:');
  return `${shrink ? TH_SHRINK : TH} z-[2]`;
}

type OpenOrder = ((orderId: string) => void) | undefined;

/** Всё, что нужно ячейке строки. */
interface CellContext {
  item: CrmTableRow;
  row: CrmRow;
  save: CrmRowSave;
  bg: string;
  timeZone: string | undefined;
  stages: readonly CrmFunnelStage[];
  managers: ReturnType<typeof useCrmRowEdit>['managers'];
  columnLabels: Record<string, string>;
  openView: OpenOrder;
  openCompose: OpenOrder;
  actions: ReactNode;
}

type CellRenderer = (ctx: CellContext) => ReactNode;

/** KAN-958: ячейка по коду столбца (порядок задаёт lib/crmTableColumns, не разметка). */
const CELLS: Record<string, CellRenderer> = {
  phone: ({ row, save, bg }) => (
    <td className={`${TD} ${STICKY_PHONE} ${PINNED_TD} ${bg} ${leadTemperatureCellAccent(row.lead_temperature)}`}>
      <CrmTextField row={row} field="phone" label="Телефон" save={save} />
    </td>
  ),
  contact: ({ row, save, bg }) => (
    <td className={`${TD} ${STICKY_CONTACT} ${PINNED_TD} ${bg}`}>
      <CrmContactField row={row} save={save} />
      <LeadTemperatureBadge level={row.lead_temperature} className="mt-1" />
    </td>
  ),
  date: ({ item }) => (
    <td className={TD_NOWRAP}>
      <CrmDateCell item={item} />
    </td>
  ),
  group: ({ row, columnLabels }) => (
    <td className={`${TD} min-w-[130px]`}>
      <CrmLeadColumnCell row={row} labels={columnLabels} />
    </td>
  ),
  days: ({ row }) => (
    <td className={TD_NOWRAP}>
      <CrmLeadDaysCell row={row} />
    </td>
  ),
  taken: ({ row }) => (
    <td className={TD_NOWRAP}>
      <CrmTakenCell row={row} />
    </td>
  ),
  reason: ({ row, timeZone }) => (
    <td className={`${TD} min-w-[120px] text-gray-700`}>
      <ReasonCell row={row} timeZone={timeZone} clamp />
      {row.next_bot_touch && (
        <div className="mt-1">
          <NextBotTouchNote clientId={row.client_id} touch={row.next_bot_touch} variant="compact" />
        </div>
      )}
    </td>
  ),
  stage: ({ row, stages, save }) => (
    <td className={`${TD} min-w-[110px]`}>
      <CrmStageField row={row} stages={stages} save={save} />
    </td>
  ),
  company: ({ row, save }) => (
    <td className={`${TD} min-w-[140px]`}>
      <CrmCompanyField row={row} save={save} />
    </td>
  ),
  city: ({ row, save }) => (
    <td className={`${TD} min-w-[100px]`}>
      <CrmTextField row={row} field="city" label="Город" save={save} />
    </td>
  ),
  telegram: ({ row, save }) => (
    <td className={`${TD_NOWRAP} min-w-[120px]`}>
      <CrmTelegramField row={row} save={save} />
    </td>
  ),
  manager: ({ row, managers, save }) => (
    <td className={`${TD} min-w-[140px]`}>
      <CrmManagerField row={row} managers={managers} save={save} />
    </td>
  ),
  next_contact: ({ row, save, timeZone }) => (
    <td className={TD_NOWRAP}>
      <CrmNextContactField row={row} save={save} timeZone={timeZone} />
    </td>
  ),
  comment: ({ row, save }) => (
    <td className={`${TD} min-w-[220px] max-w-[320px]`}>
      <CrmCommentField row={row} save={save} />
    </td>
  ),
  orders: ({ row, openView, openCompose }) => (
    <td className={`${TD} min-w-[140px]`}>
      <CrmOrdersCell current={row.current_order} orders={row.orders} onOpenCurrent={openCompose} onOpenPast={openView} />
    </td>
  ),
  actions: ({ bg, actions }) => (
    <td className={`${TD} ${STICKY_RIGHT} z-[1] ${bg}`}>
      <div className="flex flex-nowrap justify-end gap-1">{actions}</div>
    </td>
  ),
};

function attributeCell(attribute: CrmAttribute): CellRenderer {
  return function AttributeCell({ row, save }: CellContext) {
    return (
      <td className={`${TD} min-w-[90px] max-w-[260px]`}>
        <CrmAttributeField row={row} attribute={attribute} save={save} />
      </td>
    );
  };
}

interface HeaderCellProps {
  column: CrmTableColumn;
  onHide?: (code: string) => void;
  attribute: boolean;
}

/** KAN-958: шапка столбца; у скрываемого кнопка-глаз (видна при наведении на шапку и при фокусе). */
function HeaderCell({ column, onHide, attribute }: HeaderCellProps) {
  return (
    <th
      className={`${headerClass(column.code)} group`}
      data-column={column.code}
      data-testid={attribute ? 'crm-attribute-header' : undefined}
    >
      {onHide && !column.required ? (
        <span className="inline-flex items-start gap-1">
          <span>{column.label}</span>
          <button
            type="button"
            className="shrink-0 rounded text-gray-400 opacity-0 transition-opacity hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={`Скрыть колонку ${column.label}`}
            title="Скрыть колонку"
            data-testid="crm-column-hide"
            onClick={() => onHide(column.code)}
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      ) : (
        column.label
      )}
    </th>
  );
}

export function CrmTable({
  items,
  renderActions,
  testId,
  stages = [],
  fullscreen = false,
  attributes = NO_ATTRIBUTES,
  columnLabels,
  footer,
  groupByDay = false,
  hiddenColumns = NO_HIDDEN,
  onHideColumn,
}: CrmTableProps) {
  const leadMode = columnLabels !== undefined;
  const { save, managers } = useCrmRowEdit();
  // KAN-840: следующая связь и срок задачи по времени организации.
  const timeZone = useOrgTimezone();
  // KAN-957: до хука высоты: таблица до низа экрана без отступа кабинета под кнопку подсказки.
  useReleaseBottomGutter();
  // KAN-758: высота обёртки до низа экрана по фактическому положению, без calc(100vh - N).
  const fill = useFillViewportHeight<HTMLDivElement>(fullscreen ? FULLSCREEN_FILL : NORMAL_FILL);
  const orders = useCrmOrderDialogs();
  // KAN-811: заказы закрыты роли MANAGER: заказы текстом, без кнопки заказа.
  const canOrders = useCanCallApi('GET', `/api/v1/orders/${ANY_UUID}/`);
  const openView = canOrders ? orders.openView : undefined;
  const openCompose = canOrders ? orders.openCompose : undefined;

  // KAN-958: видимые столбцы в порядке определения; обязательные видны всегда.
  const columns = useMemo(
    () => crmTableColumns({ leadMode, attributes }).filter((c) => c.required || !hiddenColumns.has(c.code)),
    [leadMode, attributes, hiddenColumns]
  );
  const renderers = useMemo(() => {
    const byCode: Record<string, CellRenderer> = { ...CELLS };
    for (const attribute of attributes) byCode[attributeColumnCode(attribute.id)] = attributeCell(attribute);
    return byCode;
  }, [attributes]);
  const attributeCodes = useMemo(() => new Set(attributes.map((a) => attributeColumnCode(a.id))), [attributes]);

  const renderRow = (item: CrmTableRow) => {
    const { row } = item;
    const temperature = leadTemperatureStyle(row.lead_temperature);
    const ctx: CellContext = {
      item,
      row,
      save: (patch: CrmRowPatch) => save(row.client_id, patch),
      bg: pinnedBg(!!temperature),
      timeZone,
      stages,
      managers,
      columnLabels: columnLabels ?? NO_LABELS,
      openView,
      openCompose,
      actions: (
        <>
          {renderActions(item)}
          {canOrders && (
            <OrderActionButton
              order={row.current_order}
              creating={orders.creatingFor === row.client_id}
              onOpen={orders.openCompose}
              onCreate={() => orders.startOrder(row.client_id)}
            />
          )}
        </>
      ),
    };
    return (
      <tr
        key={`${item.kind}-${row.client_id}`}
        data-testid="crm-row"
        data-kind={tableRowKind(item)}
        className={temperature?.row}
      >
        {columns.map((column) => (
          <Fragment key={column.code}>{renderers[column.code]?.(ctx)}</Fragment>
        ))}
      </tr>
    );
  };

  const body = groupByDay
    ? groupRowsByTakenDay(items).flatMap((group, index) => [
        <tr key={`day-${group.day ?? 'none'}-${index}`} data-testid="crm-day-header">
          <td colSpan={columns.length} className={DAY_HEADER}>
            <span className="sticky left-2.5 inline-block">{group.title}</span>
          </td>
        </tr>,
        ...group.items.map(renderRow),
      ])
    : items.map(renderRow);

  return (
    <div ref={fill.ref} className="max-w-full overflow-auto" style={fill.style} data-testid={testId}>
      <table className="w-full min-w-[1400px] border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <HeaderCell
                key={column.code}
                column={column}
                onHide={onHideColumn}
                attribute={attributeCodes.has(column.code)}
              />
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
      {footer}
      {/* KAN-957: место под плавающую кнопку подсказки: последнюю строку можно прокрутить выше неё. */}
      <div aria-hidden="true" data-testid="crm-table-button-room" className="h-24" />
      <OrderViewDialog orderId={orders.viewOrderId} onClose={orders.closeView} />
      <OrderComposeDialog orderId={orders.composeOrderId} onClose={orders.closeCompose} />
    </div>
  );
}
